import { useEffect, useRef, useState } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import axios from 'axios'
import { CircularProgress } from '@mui/material'
import { useDispatch } from 'react-redux'
import { checkErrorAPICall } from '../../helpers/checkErrorAPICall'

const MONTHLY_SHIPMENTS = '/api/v1/report/monthlyshipments'

interface MonthlyData {
  month: string
  monthShort: string
  year: string
  numberOfQuotes: number
  numberOfShipments: number
}

const MonthlyShipmentsChart = () => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<MonthlyData[]>([])
  const [chart, setChart] = useState<ApexCharts | null>(null)

  const getMonthlyShipments = () => {
    setLoading(true)
    axios.get(MONTHLY_SHIPMENTS)
      .then((results) => {
        setLoading(false)
        setData(results.data.data.results)
      })
      .catch(error => {
        setLoading(false)
        checkErrorAPICall(error, dispatch, 'Monthly Shipments')
      })
  }

  useEffect(() => {
    getMonthlyShipments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!chartRef.current || !data.length) return

    const chartOptions: ApexOptions = {
      series: [
        {
          name: 'Quotes',
          data: data.map(d => d.numberOfQuotes)
        },
        {
          name: 'Shipments',
          data: data.map(d => d.numberOfShipments)
        }
      ],
      chart: {
        type: 'line',
        height: 285,
        toolbar: {
          show: true,
          tools: {
            download: false,
            selection: false,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: false,
            reset: true
          }
        },
        fontFamily: 'Poppins, sans-serif',
        zoom: {
          enabled: true
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        width: 3,
        curve: 'smooth'
      },
      xaxis: {
        categories: data.map(d => d.monthShort),
        labels: {
          style: {
            colors: '#64748b',
            fontSize: '12px'
          }
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      yaxis: {
        labels: {
          style: {
            colors: '#64748b',
            fontSize: '12px'
          },
          formatter: function (val) {
            return Math.round(val).toString()
          }
        }
      },
      colors: ['#f59e0b', '#3b82f6'],
      markers: {
        size: 5,
        colors: ['#f59e0b', '#3b82f6'],
        strokeColors: '#fff',
        strokeWidth: 2,
        hover: {
          size: 7
        }
      },
      legend: {
        show: false
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          formatter: function (val, opts) {
            const index = opts.dataPointIndex
            if (data[index]) {
              return `${data[index].month} ${data[index].year}`
            }
            return val.toString()
          }
        },
        y: {
          formatter: function (val) {
            return val.toString()
          }
        }
      },
      grid: {
        borderColor: '#e2e8f0',
        strokeDashArray: 0,
        xaxis: {
          lines: {
            show: true
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        }
      }
    }

    if (chart) {
      chart.destroy()
    }

    const newChart = new ApexCharts(chartRef.current, chartOptions)
    newChart.render()
    setChart(newChart)

    return () => {
      newChart.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const hasData = data.some(d => d.numberOfShipments > 0 || d.numberOfQuotes > 0)

  return (
    <div className="row bg-white p-2 borderAndShadow">
      <div className="col-12 d-flex align-items-center" style={{ height: 43 }}>
        <label className='col-form-label w-75'>Monthly Shipments</label>
        <div className="w-25 d-flex justify-content-end align-items-center" style={{ gap: '12px' }}>
          <div className="d-flex align-items-center">
            <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#f59e0b', marginRight: 6 }}></div>
            <span style={{ fontSize: '12px', color: '#A1A5B7' }}>Quotes</span>
          </div>
          <div className="d-flex align-items-center">
            <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#3b82f6', marginRight: 6 }}></div>
            <span style={{ fontSize: '12px', color: '#A1A5B7' }}>Shipments</span>
          </div>
        </div>
      </div>
      <div className="col-12">
        {loading ? (
          <div className='d-flex align-items-center justify-content-center' style={{ height: 300 }}>
            <CircularProgress style={{ color: '#3f4254' }} size={30} />
          </div>
        ) : hasData ? (
          <div ref={chartRef} />
        ) : (
          <div className='d-flex align-items-center justify-content-center' style={{ height: 300 }}>
            No results
          </div>
        )}
      </div>
    </div>
  )
}

export default MonthlyShipmentsChart
